'use client';

import {
  generateFidelity,
  getCryptoName,
  getRecurrence,
  crypto2Symbol,
  generateInterval,
} from '@/components/predic/detail/market/crypto-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Skeleton } from '@mui/material';
import * as echarts from 'echarts';
import { useTheme } from 'next-themes';
import { currencyFormatter, numberFormatter } from '@/lib/utils';
import ResolvedButtons from './ResolvedButton';
import { BaseChartOptions, grid, tooltip, xAxis, yAxis } from './BaseChartConfig';
import { useCryptoOpenPrice } from '@/hooks/use-crypto-open-price';
import { KlineFidelity, useTokenKline } from '@/hooks/use-token-kline';
import { useRealTimePrice } from '@/hooks/use-real-time-price';
import { EventData, PolymarketMarket } from '../detail/market/types';
import EChart from '@/components/common/EChart';
import { useMarketRealtimeSubscriptions } from '@/hooks/data/useMarketRealtimeSubscriptions';
import { useMarketRealtimeStore } from '@/hooks/store/useMarketRealtimeStore';
import { usePolymarketRealtime } from '@/hooks/usePolymarketRealtime';
import dayjs from 'dayjs';
import { formatEndTime } from '../hooks/use-time-window-list';
import { parseSlug2EndTimestamp, recurrenceToMs } from '../detail/market/util';

interface RealtimeProbabilityPoint {
  timestampMs: number;
  probability: number;
}

function normalizeProbability(value: number): number | undefined {
  if (!Number.isFinite(value) || value < 0) return undefined;
  if (value > 1 && value <= 100) return value / 100;
  if (value > 100) return undefined;
  return value;
}

function isPastEndDate(endDate?: string | null): boolean {
  const endTime = Date.parse(String(endDate ?? ''));
  return Number.isFinite(endTime) && Date.now() > endTime;
}

function MarketRealtimeRecorder({
  marketId,
  markets,
  onPoint,
}: {
  marketId?: string;
  markets?: PolymarketMarket[];
  onPoint: (point: RealtimeProbabilityPoint) => void;
}) {
  useMarketRealtimeSubscriptions(markets);
  const realtimeMarket = useMarketRealtimeStore((state) =>
    marketId ? state.marketsById[marketId] : undefined,
  );
  const realtimeProbability = realtimeMarket?.yesPrice;

  console.log(realtimeProbability);

  useEffect(() => {
    if (realtimeProbability === undefined) return;

    const probability = normalizeProbability(realtimeProbability);
    const timestampMs = realtimeMarket?.updatedAt ?? Date.now();
    if (probability === undefined || !Number.isFinite(timestampMs)) return;

    onPoint({ timestampMs, probability });
  }, [onPoint, realtimeMarket?.updatedAt, realtimeProbability]);

  return null;
}

export default function CryptoTrendChart({
  eventData,
  openInSidebar,
}: {
  eventData: EventData;
  openInSidebar?: boolean;
}) {
  const cryptoName = getCryptoName(eventData.tags);
  const recurrence = getRecurrence(eventData.tags);

  const marketItem = eventData.markets[0];
  const resolutionSource = eventData.event.resolution_source;

  const { theme } = useTheme();

  const cryptoSymbol = crypto2Symbol[cryptoName];
  const isResolved =
    !!eventData.event.closed ||
    eventData.markets?.[0]?.uma_resolution_status === 'resolved' ||
    isPastEndDate(eventData.event.end_date);
  const endTime = parseSlug2EndTimestamp(eventData?.event?.slug, recurrence) / 1000;
  const startTime = endTime - recurrenceToMs[recurrence] / 1000;
  const fidelity = generateFidelity(recurrence);
  const interval = generateInterval(recurrence);

  const currentWindow = useMemo(
    () => ({
      slug: eventData?.event?.slug,
      startTs: dayjs(eventData?.event.start_date).unix(),
      endTs: dayjs(eventData?.event.end_date).unix(),
      label: formatEndTime(dayjs(eventData?.event.end_date).unix()),
      token: cryptoSymbol,
      active: false,
    }),
    [eventData?.event, cryptoSymbol],
  );

  const { priceData } = usePolymarketRealtime({
    token: cryptoSymbol,
    interval: recurrence as any,
    eventSlug: eventData.event.slug,
    enabled: !isResolved,
  });

  const { data: klineData } = useTokenKline({
    token: isResolved ? cryptoSymbol : undefined,
    // interval: interval,
    fidelity: generateFidelity(recurrence) as KlineFidelity,
    resolved: isResolved,
    end_time: endTime,
    start_time: startTime,
  });

  // useRealTimePrice 的 interval 单位是秒，generateFidelity 对 5m/15m 返回 '1s'
  const klineInterval = fidelity === '1s' ? 1 : Number(fidelity) || 60;

  const { data: marketPriceHistory } = useRealTimePrice({
    marketKey: eventData.event.slug,
    interval: 10, // klineInterval,
    start: startTime,
    end: endTime,
  });

  const [realtimeProbabilityPoints, setRealtimeProbabilityPoints] = useState<
    RealtimeProbabilityPoint[]
  >([]);

  const openPrice = useCryptoOpenPrice({
    token: crypto2Symbol[cryptoName],
    slug: eventData.event.slug,
    openTime: startTime,
    source: resolutionSource.startsWith('https://data.chain.link') ? 'chainlink' : 'binance',
  });

  const fallbackProbability = Number(marketItem?.outcome_yes_price);

  useEffect(() => {
    setRealtimeProbabilityPoints([]);
  }, [marketItem?.market_id]);

  const recordRealtimeProbabilityPoint = useCallback((point: RealtimeProbabilityPoint) => {
    setRealtimeProbabilityPoints((prev) => {
      const last = prev.at(-1);
      if (last?.timestampMs === point.timestampMs && last.probability === point.probability) {
        return prev;
      }
      if (last?.probability === point.probability) {
        return prev;
      }

      return [...prev, point].slice(-1000);
    });
  }, []);

  const chartData = useMemo(() => {
    const cryptoHistory = isResolved
      ? [...(klineData?.history ?? [])]
          .map((item) => ({
            timestampMs: Number(item.t) * 1000,
            price: Number(item.close_price),
          }))
          .filter((item) => Number.isFinite(item.timestampMs) && Number.isFinite(item.price))
          .sort((a, b) => a.timestampMs - b.timestampMs)
      : [...priceData]
          .map((item) => ({
            timestampMs: Number(item.timestamp),
            price: Number(item.price),
          }))
          .filter((item) => Number.isFinite(item.timestampMs) && Number.isFinite(item.price))
          .sort((a, b) => a.timestampMs - b.timestampMs);

    const marketHistory = [
      ...(marketPriceHistory?.data ?? []).map((item) => ({
        timestampMs: Number(item.startTime) * 1000,
        probability: Number(item.close),
      })),
      ...realtimeProbabilityPoints,
    ]
      .map((item) => ({
        ...item,
        probability: normalizeProbability(item.probability),
      }))
      .filter(
        (item): item is RealtimeProbabilityPoint =>
          Number.isFinite(item.timestampMs) && item.probability !== undefined,
      )
      .sort((a, b) => a.timestampMs - b.timestampMs);

    const fallback = Number.isFinite(fallbackProbability) ? fallbackProbability : 0;
    const timeline = Array.from(
      new Set([
        ...cryptoHistory.map((item) => item.timestampMs),
        ...marketHistory.map((item) => item.timestampMs),
      ]),
    ).sort((a, b) => a - b);

    let cryptoIndex = 0;
    let marketIndex = 0;
    let currentProbability = normalizeProbability(fallback) ?? 0;

    return timeline.flatMap((timestampMs) => {
      while (
        cryptoIndex + 1 < cryptoHistory.length &&
        cryptoHistory[cryptoIndex + 1].timestampMs <= timestampMs
      ) {
        cryptoIndex += 1;
      }
      while (
        marketIndex < marketHistory.length &&
        marketHistory[marketIndex].timestampMs <= timestampMs
      ) {
        currentProbability = marketHistory[marketIndex].probability;
        marketIndex += 1;
      }

      const cryptoPoint = cryptoHistory[cryptoIndex];
      const price =
        cryptoPoint && cryptoPoint.timestampMs <= timestampMs ? cryptoPoint.price : undefined;

      return [
        {
          timestamp: new Date(timestampMs).toISOString(),
          price,
          probability: currentProbability <= 1 ? currentProbability * 100 : currentProbability,
        },
      ];
    });
  }, [
    fallbackProbability,
    isResolved,
    klineData?.history,
    marketPriceHistory?.data,
    priceData,
    realtimeProbabilityPoints,
  ]);

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (!chartData.length) return null;

    const yesSeries: echarts.LineSeriesOption = {
      id: 'yes',
      data: chartData
        .filter((item) => Number.isFinite(item.probability))
        .map((item) => [item.timestamp, item.probability]),
      name: marketItem?.group_item_title || 'YES',
      type: 'line',
      showSymbol: false,
      smooth: true,
      sampling: 'lttb',
      yAxisIndex: 1,
      itemStyle: { color: '#FFBE49' },
      lineStyle: { width: 1.5 },
      tooltip: {
        valueFormatter: function (value: any) {
          return `${value.toFixed(1)}%`;
        },
      },
    };

    const cryptoPriceValues: number[] = [];
    const cryptoSeries: echarts.LineSeriesOption = {
      id: 'crypto',
      data: chartData
        .filter((item): item is typeof item & { price: number } => Number.isFinite(item.price))
        .map((item) => {
          cryptoPriceValues.push(item.price);
          return [item.timestamp, item.price];
        }),
      name: cryptoName,
      type: 'line',
      showSymbol: false,
      smooth: true,
      sampling: 'lttb',
      yAxisIndex: 0,
      itemStyle: { color: '#6FCF97' },
      tooltip: {
        valueFormatter: function (value: any) {
          return `$${numberFormatter(value)}`;
        },
      },
      lineStyle: { width: 1.5 },
      markLine: {
        label: {
          position: 'insideStartTop',
          distance: 4,
          color: '#6FCF97',
          formatter: () => `${currencyFormatter(Number(openPrice), { notation: 'standard' })}`,
        },
        lineStyle: { color: '#6FCF9780' },
        data: [
          openPrice
            ? {
                name: '',
                symbol: 'none',
                yAxis: +openPrice,
              }
            : null,
        ].filter(Boolean) as any,
      },
    };

    const yesValues = chartData.map((item) => item.probability);
    const yMax = yesValues.length ? Math.min(100, Math.ceil(Math.max(...yesValues))) : 100;
    const openPriceValue = Number(openPrice);
    const priceAxisValues = [
      ...cryptoPriceValues,
      ...(Number.isFinite(openPriceValue) && openPriceValue > 0 ? [openPriceValue] : []),
    ];
    const priceAxisMin = priceAxisValues.length ? Math.min(...priceAxisValues) : undefined;
    const priceAxisMax = priceAxisValues.length ? Math.max(...priceAxisValues) : undefined;

    return {
      ...BaseChartOptions,
      // 实时数据每 tick 都会重算 option，用匀速更新动画让线条平滑延伸
      animationDurationUpdate: 300,
      animationEasingUpdate: 'linear' as const,
      xAxis: [
        {
          ...xAxis,
          type: 'time',
        } as echarts.XAXisComponentOption,
      ],
      yAxis: [
        {
          ...yAxis,
          name: `${cryptoSymbol} Price`,
          min: priceAxisMin,
          max: priceAxisMax,
          splitLine: {
            show: false,
            lineStyle: { color: theme === 'dark' ? '#2c2c2f' : '#e9e9e9', type: 'dashed' },
          },
          axisLabel: {
            color: '#999',
            formatter: (v: any) => `${currencyFormatter(v, { notation: 'standard' })}`,
          },
        },
        {
          ...yAxis,
          name: 'YES Price',
          min: 0,
          max: yMax,
          splitLine: {
            show: true,
            lineStyle: { color: theme === 'dark' ? '#2c2c2f' : '#e9e9e9', type: 'dashed' },
          },
          axisLabel: { color: '#999', formatter: (v: any) => `${v}%` },
        },
      ],
      grid,
      tooltip,
      series: [yesSeries, cryptoSeries],
    };
  }, [chartData, cryptoName, cryptoSymbol, marketItem?.group_item_title, openPrice, theme]);

  if (!chartData.length) {
    return (
      <>
        {!isResolved && (
          <MarketRealtimeRecorder
            marketId={marketItem?.market_id}
            markets={eventData?.markets}
            onPoint={recordRealtimeProbabilityPoint}
          />
        )}
        <Skeleton
          variant="rounded"
          width={'100%'}
          height={260}
          sx={{ bgcolor: 'grey.800', marginTop: 2 }}
        />
      </>
    );
  }

  return (
    <div>
      {!isResolved && (
        <MarketRealtimeRecorder
          marketId={marketItem?.market_id}
          markets={eventData?.markets}
          onPoint={recordRealtimeProbabilityPoint}
        />
      )}
      <EChart
        option={option}
        notMerge={false}
        className="w-full min-h-65"
        openInSidebar={openInSidebar}
      />
      <ResolvedButtons
        window={currentWindow}
        onChange={function (timestamp: number): void {
          throw new Error('Function not implemented.');
        }}
      />
    </div>
  );
}
